import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
    Users, FolderKanban, Coins, UserCircle2, ArrowUpRight, Plus, Play,
} from "lucide-react";
import { STAGES, stageStatusColor } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { CountUp } from "@/components/CountUp";
import ParticlesBg from "@/components/ParticlesBg";
import StatusDonut from "@/components/StatusDonut";
import ActivityFeed from "@/components/ActivityFeed";
import GenieEmpty from "@/components/GenieEmpty";
import DashboardCeoCard from "@/components/DashboardCeoCard";

/** ───────── Phase B Living Dashboard ─────────
 *  - CSS-only floating particles backdrop.
 *  - Top metric bar: Credits · Active Projects · Clients · Profile.
 *  - Animated counters (rAF count-up).
 *  - Project Overview donut with clickable status segments.
 *  - AI Activity Feed (auto-refresh 12s).
 *  - Embedded CEO welcome card with quick suggestions.
 *  - Quick Actions: Create Project / Resume Project.
 *  - Every visible element is interactive (cards / numbers / chart segments
 *    drill into the right destination).
 *  - Zero changes to pipeline, agents, or exports. */
export default function Dashboard() {
    const [s, setS] = useState(null);
    const [cost, setCost] = useState(null);
    const [billing, setBilling] = useState(null);
    const { user } = useAuth();
    const [agency, setAgency] = useState(null);
    const nav = useNavigate();

    useEffect(() => {
        api.get("/dashboard/stats").then(({ data }) => setS(data));
        // AI cost is admin-only telemetry — don't even fetch it for non-admins.
        if (user?.is_admin) {
            api.get("/dashboard/cost").then(({ data }) => setCost(data)).catch(() => {});
        }
        api.get("/me/billing").then(({ data }) => setBilling(data)).catch(() => {});
        api.get("/agency").then(({ data }) => {
            setAgency(data);
            // Phase K-demo — CEO full-screen first-view. Redirect the user to
            // the onboarding flow when identity isn't approved yet AND they
            // haven't explicitly dismissed it this session. Persistent skips
            // live on the Dashboard CEO card (localStorage-scoped) instead.
            const status = (data?.identity_status || "").toLowerCase();
            const sessionSkipped = sessionStorage.getItem("aivos_ceo_onboard_session_dismissed") === "1";
            const persistentSkipped = localStorage.getItem("aivos_agency_onboarding_skipped") === "1";
            if (status !== "approved" && !sessionSkipped && !persistentSkipped) {
                nav("/ceo/onboard", { replace: true });
            }
        }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.is_admin]);

    const segments = useMemo(() => buildOverviewSegments(s), [s]);
    const totalProjects = (s?.active_projects || 0) + (s?.completed_projects || 0);

    const onSegmentClick = (key) => {
        // Drill into the projects list (filtered by status if we add that
        // route later). For now, route to /projects with a hash hint.
        nav(`/projects#${key}`);
    };

    const resumeTarget = (s?.recent_projects || [])
        .find(p => p.current_stage !== "final_delivery")
        ?? s?.recent_projects?.[0];

    return (
        <div className="relative min-h-full" data-testid="dashboard-page">
            {/* Particles backdrop (decorative, behind content) */}
            <ParticlesBg />

            <div className="relative z-10 p-6 sm:p-8 lg:p-10 max-w-7xl mx-auto">
                {/* Top metric bar */}
                <div
                    className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6"
                    data-testid="dashboard-topbar"
                >
                    {user?.is_admin ? (
                        <TopTile
                            testid="topbar-credits"
                            icon={Coins}
                            label="AI Spend"
                            primary={cost ? `$${cost.total_cost_usd.toFixed(4)}` : "—"}
                            sub={cost ? `${cost.total_runs} runs · admin view` : "loading…"}
                            onClick={() => nav("/admin")}
                        />
                    ) : (
                        <TopTile
                            testid="topbar-plan"
                            icon={Coins}
                            label="Plan"
                            primary={billing ? billing.plan_label : "—"}
                            primaryClass="font-display text-2xl font-medium truncate"
                            sub={
                                billing && billing.plan
                                    ? `${billing.campaigns_remaining}/${billing.campaign_quota} campaigns · ${billing.clients_remaining}/${billing.client_quota} clients left`
                                    : "Awaiting plan from admin"
                            }
                            onClick={() => nav("/settings")}
                        />
                    )}
                    <TopTile
                        testid="topbar-active"
                        icon={FolderKanban}
                        label="Active Projects"
                        primary={
                            s ? <CountUp value={s.active_projects} className="font-display text-3xl font-medium" /> : "—"
                        }
                        sub={s ? `${s.completed_projects} shipped · ${s.pending_approvals || 0} stages await approval` : "loading…"}
                        onClick={() => nav("/projects")}
                    />
                    <TopTile
                        testid="topbar-clients"
                        icon={Users}
                        label="Clients"
                        primary={
                            s ? <CountUp value={s.total_clients} className="font-display text-3xl font-medium" /> : "—"
                        }
                        sub="Manage roster →"
                        onClick={() => nav("/clients")}
                    />
                    <TopTile
                        testid="topbar-profile"
                        icon={UserCircle2}
                        label="Profile"
                        primary={(user?.name || user?.email?.split("@")[0] || "—").trim()}
                        primaryClass="font-display text-xl font-medium truncate"
                        sub={(agency?.agency_name || "Set up agency →").trim()}
                        onClick={() => nav("/settings")}
                    />
                </div>

                {/* Hero band: title + quick actions */}
                <div className="flex items-end justify-between gap-6 mb-6 flex-wrap">
                    <div>
                        <div className="label-micro mb-2">Studio Overview</div>
                        <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight">Mission Control</h1>
                        <p className="text-zinc-500 mt-2 text-sm">Every active brief, agent, and stage in one cockpit.</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="dash-quick-create"
                            onClick={() => nav("/projects/new")}
                            className="btn-accent text-sm"
                        >
                            <Plus className="w-4 h-4" /> Create Project
                        </button>
                        {resumeTarget && (
                            <button
                                data-testid="dash-quick-resume"
                                onClick={() => nav(`/projects/${resumeTarget.id}`)}
                                className="btn-secondary text-sm"
                            >
                                <Play className="w-4 h-4" /> Resume "{truncate(resumeTarget.title, 18)}"
                            </button>
                        )}
                    </div>
                </div>

                {/* Project Overview + CEO card */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                    <div className="lg:col-span-2 card-base p-6" data-testid="project-overview-card">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="label-micro">Project Overview</div>
                                <h2 className="font-display text-xl mt-1">By status</h2>
                            </div>
                            <Link
                                to="/projects"
                                data-testid="dash-view-all-projects"
                                className="text-xs font-mono text-zinc-400 hover:text-white flex items-center gap-1"
                            >
                                VIEW ALL <ArrowUpRight className="w-3 h-3" />
                            </Link>
                        </div>
                        {s ? (
                            <StatusDonut
                                segments={segments}
                                total={totalProjects}
                                onSegmentClick={onSegmentClick}
                            />
                        ) : (
                            <div className="text-zinc-500 text-sm py-12 text-center">Loading…</div>
                        )}
                    </div>
                    <DashboardCeoCard stats={s} />
                </div>

                {/* AI Activity Feed + Recent Projects */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                    <div className="lg:col-span-2 card-base p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="label-micro">AI Activity Feed</div>
                                <h2 className="font-display text-xl mt-1">Live agent runs</h2>
                            </div>
                            <span className="flex items-center gap-2 text-[11px] font-mono text-zinc-500">
                                <span className="w-2 h-2 rounded-full bg-emerald-400 pulse-dot" />
                                STREAMING
                            </span>
                        </div>
                        <ActivityFeed limit={12} />
                    </div>

                    <div className="card-base p-6">
                        <div className="label-micro mb-3">Recent Projects</div>
                        <div className="space-y-2">
                            {(s?.recent_projects || []).slice(0, 5).map(p => (
                                <Link
                                    key={p.id}
                                    to={`/projects/${p.id}`}
                                    data-testid={`dash-project-${p.id}`}
                                    className="block p-3 border border-zinc-900 hover:border-zinc-700 hover:bg-zinc-950 transition-colors rounded-sm"
                                >
                                    <div className="text-sm font-medium truncate">{p.title}</div>
                                    <div className="text-[10px] font-mono mt-1 uppercase tracking-wider text-zinc-500 flex items-center justify-between gap-2">
                                        <span className="truncate">{p.project_type?.replaceAll("_", " ")}</span>
                                        <span className={`px-1.5 py-0.5 rounded-sm border ${stageStatusColor("pending")}`}>
                                            {STAGES.find(x => x.key === p.current_stage)?.label || p.current_stage}
                                        </span>
                                    </div>
                                </Link>
                            ))}
                            {(!s?.recent_projects || s.recent_projects.length === 0) && (
                                <GenieEmpty
                                    title="No Furmais yet."
                                    subtitle="Tell me your Furmais — let's shape your first one."
                                    ctaTo="/projects/new"
                                    className="border border-dashed border-zinc-900 rounded-sm"
                                    data-testid="dashboard-recent-empty"
                                />
                            )}
                        </div>
                    </div>
                </div>

                {/* Pipeline-by-stage tiles (clickable into /projects) */}
                <div className="card-base p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="label-micro">Pipeline by Stage</div>
                        <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-600">
                            Tap a stage →
                        </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                        {STAGES.slice(1, 13).map(st => (
                            <button
                                key={st.key}
                                type="button"
                                data-testid={`dash-stage-${st.key}`}
                                onClick={() => nav(`/projects#${st.key}`)}
                                className="border border-zinc-900 hover:border-[#FF4400]/60 p-3 rounded-sm text-left transition-colors group"
                            >
                                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono group-hover:text-zinc-300">
                                    {st.label}
                                </div>
                                <div className="font-display text-2xl mt-1">
                                    <CountUp value={(s?.by_stage || {})[st.key] || 0} />
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ──────────────── helpers ──────────────── */

function TopTile({ testid, icon: Icon, label, primary, primaryClass, sub, onClick }) {
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onClick}
            className="card-base p-4 text-left card-hover hover:border-[#FF4400]/40 group"
        >
            <div className="flex items-start justify-between">
                <div className="min-w-0">
                    <div className="label-micro mb-1.5">{label}</div>
                    <div className={primaryClass || "font-display text-3xl font-medium"}>
                        {primary}
                    </div>
                </div>
                <Icon className="w-4 h-4 text-zinc-600 group-hover:text-[#FF4400]" />
            </div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-600 mt-2">
                {sub}
            </div>
        </button>
    );
}

function buildOverviewSegments(s) {
    if (!s) return [];
    // Project-level counts only (avoid mixing stage-level pending_approvals
    // with project-level active/completed counts in the same donut).
    const active = s.active_projects || 0;
    const done = s.completed_projects || 0;
    return [
        { key: "in_progress", label: "In Progress", value: active, color: "#FF4400" },
        { key: "completed", label: "Completed", value: done, color: "#10B981" },
    ];
}

function truncate(s, n) {
    if (!s) return "";
    return s.length > n ? s.slice(0, n - 1) + "…" : s;
}
